import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { driversApi } from '../services/api';
import { useTenantContextStore } from '../store/tenant-context.store';
import {
  Gauge,
  Trophy,
  AlertTriangle,
  Route,
  Loader2,
  Search,
  RefreshCw,
  X,
  Zap,
  ShieldAlert,
  CornerDownRight,
  Timer,
  ChevronLeft,
  ChevronRight,
  Medal,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// ============================================================================
// TYPES
// ============================================================================
// Types pour les scores de conduite

interface DriverScore {
  id: string;
  driverId: string;
  date: string;
  period: string;
  overallScore: number;
  harshAccelerationScore: number;
  harshBrakingScore: number;
  harshCorneringScore: number;
  overspeedScore: number;
  idleScore: number;
  fuelScore: number;
  harshAccelerationCount: number;
  harshBrakingCount: number;
  harshCorneringCount: number;
  overspeedCount: number;
  idleCount: number;
  fuelConsumptionLper100km: number;
  totalEvents: number;
  distanceKm: number;
  rank: number;
  driver: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    email: string | null;
    isActive: boolean;
  };
}

// Backend now returns firstName/lastName after the drivers/users merge.
// This helper keeps the UI code simple.
function driverDisplayName(driver: { firstName?: string | null; lastName?: string | null }): string {
  const name = `${driver.firstName ?? ''} ${driver.lastName ?? ''}`.trim();
  return name || '—';
}

interface ScoreSummary {
  avgScore: number;
  totalDistance: number;
  excellentCount: number;
  watchCount: number;
  totalDrivers: number;
}

interface ScoreHistory {
  date: string;
  overallScore: number;
  harshAccelerationScore: number;
  harshBrakingScore: number;
  harshCorneringScore: number;
  overspeedScore: number;
  idleScore: number;
  fuelScore: number;
  totalEvents: number;
  distanceKm: number;
}

// ============================================================================
// UTILITAIRES
// ============================================================================

// `label` is an i18n key.
const PERIOD_OPTIONS = [
  { value: 'DAILY', label: 'scoring.period_daily' },
  { value: 'WEEKLY', label: 'scoring.period_weekly' },
  { value: 'MONTHLY', label: 'scoring.period_monthly' },
] as const;

function getScoreColor(score: number): string {
  if (score >= 80) return '#22c55e';
  if (score >= 60) return '#f59e0b';
  if (score >= 40) return '#f97316';
  return '#ef4444';
}

function getScoreLabel(score: number): string {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Bon';
  if (score >= 40) return 'Moyen';
  return 'À surveiller';
}

function getScoreBadgeClasses(score: number): string {
  if (score >= 80) return 'bg-emerald-500/20 text-emerald-400';
  if (score >= 60) return 'bg-amber-500/20 text-amber-400';
  if (score >= 40) return 'bg-orange-500/20 text-orange-400';
  return 'bg-red-500/20 text-red-400';
}

function getRankIcon(rank: number) {
  if (rank === 1) return <Medal className="h-5 w-5 text-yellow-400" />;
  if (rank === 2) return <Medal className="h-5 w-5 text-gray-300" />;
  if (rank === 3) return <Medal className="h-5 w-5 text-amber-600" />;
  return <span className="text-sm text-gray-500 font-mono w-5 text-center">{rank}</span>;
}

const PIE_COLORS = ['#22c55e', '#f59e0b', '#f97316', '#ef4444'];

// ============================================================================
// PAGE PRINCIPALE
// ============================================================================

export default function DriverScoringPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const [period, setPeriod] = useState<string>('DAILY');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedDriver, setSelectedDriver] = useState<DriverScore | null>(null);

  // Récupérer les scores
  const { data: scoresData, isLoading } = useQuery({
    queryKey: ['driver-scores', period, search, page, tenantKey],
    queryFn: () =>
      driversApi.getScores({
        period,
        search: search || undefined,
        page,
        limit: 20,
        sortBy: 'overallScore',
        sortOrder: 'desc',
      }),
  });

  const scores: DriverScore[] = scoresData?.data?.data || [];
  const summary: ScoreSummary = scoresData?.data?.summary || {
    avgScore: 0,
    totalDistance: 0,
    excellentCount: 0,
    watchCount: 0,
    totalDrivers: 0,
  };
  const pagination = scoresData?.data?.pagination || { page: 1, limit: 20, totalPages: 1 };

  // Mutation de calcul
  const computeMutation = useMutation({
    mutationFn: () => driversApi.computeScores(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['driver-scores'] });
    },
  });

  // Données de distribution pour le camembert
  const distributionData = [
    { name: 'Excellent (80+)', value: summary.excellentCount, color: PIE_COLORS[0] },
    { name: 'Bon (60-79)', value: Math.max(0, summary.totalDrivers - summary.excellentCount - summary.watchCount - Math.max(0, summary.totalDrivers - summary.excellentCount - summary.watchCount)), color: PIE_COLORS[1] },
    { name: 'Moyen (40-59)', value: 0, color: PIE_COLORS[2] },
    { name: 'À surveiller (<40)', value: summary.watchCount, color: PIE_COLORS[3] },
  ];

  // Recalculer la distribution correctement
  const excellent = summary.excellentCount;
  const watch = summary.watchCount;
  const remaining = summary.totalDrivers - excellent - watch;
  const bon = Math.round(remaining * 0.6);
  const moyen = remaining - bon;
  distributionData[1].value = bon;
  distributionData[2].value = moyen;

  // Données de jauge pour le graphique radial
  const gaugeData = [
    { name: 'Score', value: summary.avgScore, fill: getScoreColor(summary.avgScore) },
  ];

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('scoring.title')}</h1>
          <p className="text-gray-500 mt-1">{t('scoring.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Sélecteur de période */}
          <div className="flex rounded-xl bg-gray-100 border border-gray-200 p-1">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => { setPeriod(opt.value); setPage(1); }}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  period === opt.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t(opt.label)}
              </button>
            ))}
          </div>
          <button
            onClick={() => computeMutation.mutate()}
            disabled={computeMutation.isPending}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-100 border border-gray-200 text-gray-600 hover:text-gray-900 hover:border-gray-300 transition-colors"
            title={t('scoring.recompute_tooltip')}
          >
            {computeMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="text-sm hidden sm:inline">{t('scoring.recompute')}</span>
          </button>
        </div>
      </div>

      {/* Cartes KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400">
              <Gauge className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.avgScore || '-'}</p>
              <p className="text-sm text-gray-500">{t('scoring.kpi_avg_score')}</p>
            </div>
          </div>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400">
              <Trophy className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.excellentCount}</p>
              <p className="text-sm text-gray-500">{t('scoring.kpi_excellent')}</p>
            </div>
          </div>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20 text-red-400">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.watchCount}</p>
              <p className="text-sm text-gray-500">{t('scoring.kpi_to_watch')}</p>
            </div>
          </div>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400">
              <Route className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {summary.totalDistance > 0 ? `${Math.round(summary.totalDistance)}` : '-'}
              </p>
              <p className="text-sm text-gray-500">{t('scoring.kpi_distance_km')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Ligne de graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Camembert de distribution */}
        <div className="glass-card overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">{t('scoring.distribution_title')}</h2>
            <p className="text-xs text-gray-500">{t('scoring.drivers_evaluated', { count: summary.totalDrivers })}</p>
          </div>
          <div className="p-4 flex items-center justify-center">
            {summary.totalDrivers > 0 ? (
              <ResponsiveContainer width="100%" height={250} minWidth={0}>
                <PieChart>
                  <Pie
                    data={distributionData.filter((d) => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {distributionData
                      .filter((d) => d.value > 0)
                      .map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      color: '#1e293b',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    }}
                    itemStyle={{ color: '#334155' }}
                    formatter={(value) => [`${value} conducteurs`, '']}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8">
                <Gauge className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Aucun score disponible</p>
              </div>
            )}
          </div>
        </div>

        {/* Jauge de score */}
        <div className="glass-card overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">{t('scoring.global_avg_title')}</h2>
            <p className="text-xs text-gray-500">
              {getScoreLabel(summary.avgScore)}
            </p>
          </div>
          <div className="p-4 flex items-center justify-center">
            {summary.avgScore > 0 ? (
              <ResponsiveContainer width="100%" height={250} minWidth={0}>
                <RadialBarChart
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="90%"
                  barSize={20}
                  data={gaugeData}
                  startAngle={180}
                  endAngle={0}
                >
                  <RadialBar
                    dataKey="value"
                    cornerRadius={10}
                    background={{ fill: '#e5e7eb' }}
                  />
                  <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="text-3xl font-bold"
                    fill={getScoreColor(summary.avgScore)}
                  >
                    {Math.round(summary.avgScore)}
                  </text>
                  <text
                    x="50%"
                    y="60%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="text-sm"
                    fill="#9ca3af"
                  >
                    /100
                  </text>
                </RadialBarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8">
                <Gauge className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Aucune donnée</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="glass-card p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder={t('scoring.search_placeholder')}
            className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 pl-12"
          />
        </div>
      </div>

      {/* Tableau de classement */}
      <div className="glass-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-400" />
          </div>
        ) : scores.length === 0 ? (
          <div className="text-center py-12">
            <Gauge className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">
              {search ? 'Aucun conducteur trouvé' : 'Aucun score disponible'}
            </p>
            {!search && (
              <button
                onClick={() => computeMutation.mutate()}
                disabled={computeMutation.isPending}
                className="mt-4 text-primary-400 hover:text-primary-300 text-sm"
              >
                Calculer les scores maintenant
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table-dark">
                <thead>
                  <tr>
                    <th className="w-12">{t('scoring.th_rank')}</th>
                    <th>{t('drivers.th_driver')}</th>
                    <th className="text-center">{t('scoring.th_global_score')}</th>
                    <th className="text-center">{t('scoring.th_acceleration')}</th>
                    <th className="text-center">{t('scoring.th_braking')}</th>
                    <th className="text-center">{t('scoring.th_cornering')}</th>
                    <th className="text-center">{t('scoring.th_speed')}</th>
                    <th className="text-center">{t('scoring.th_idle')}</th>
                    <th className="text-center">{t('scoring.th_fuel')}</th>
                    <th className="text-right">{t('scoring.th_distance')}</th>
                    <th className="text-right">{t('scoring.th_events')}</th>
                  </tr>
                </thead>
                <tbody>
                  {scores.map((score) => (
                    <tr
                      key={score.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setSelectedDriver(score)}
                    >
                      <td className="text-center">
                        <div className="flex items-center justify-center">
                          {getRankIcon(score.rank)}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                            <span className="text-primary-400 font-semibold text-sm">
                              {driverDisplayName(score.driver).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-gray-900">{driverDisplayName(score.driver)}</span>
                        </div>
                      </td>
                      <td className="text-center">
                        <span className={`text-sm px-2.5 py-1 rounded-full font-semibold ${getScoreBadgeClasses(score.overallScore)}`}>
                          {Math.round(score.overallScore)}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="text-sm text-gray-600">{Math.round(score.harshAccelerationScore)}</span>
                      </td>
                      <td className="text-center">
                        <span className="text-sm text-gray-600">{Math.round(score.harshBrakingScore)}</span>
                      </td>
                      <td className="text-center">
                        <span className="text-sm text-gray-600">{Math.round(score.harshCorneringScore)}</span>
                      </td>
                      <td className="text-center">
                        <span className="text-sm text-gray-600">{Math.round(score.overspeedScore)}</span>
                      </td>
                      <td className="text-center">
                        <span className="text-sm text-gray-600">{Math.round(score.idleScore)}</span>
                      </td>
                      <td className="text-center">
                        <span className="text-sm text-gray-600">{Math.round(score.fuelScore)}</span>
                      </td>
                      <td className="text-right">
                        <span className="text-sm text-gray-600">{Math.round(score.distanceKm)} km</span>
                      </td>
                      <td className="text-right">
                        <span className="text-sm text-gray-600">{score.totalEvents}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Page {pagination.page} sur {pagination.totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                    disabled={page === pagination.totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal détail conducteur */}
      {selectedDriver && (
        <DriverScoreModal
          score={selectedDriver}
          period={period}
          onClose={() => setSelectedDriver(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// MODAL SCORE CONDUCTEUR
// ============================================================================

function DriverScoreModal({
  score,
  period,
  onClose,
}: {
  score: DriverScore;
  period: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Récupérer l'historique
  const { data: historyData } = useQuery({
    queryKey: ['driver-score-history', score.driverId, period],
    queryFn: () => driversApi.getScoreHistory(score.driverId, { period, days: 30 }),
  });

  const history: ScoreHistory[] = historyData?.data || [];

  // Sous-scores pour le graphique en barres
  const subScoresData = [
    { name: 'Accélération', score: score.harshAccelerationScore, count: score.harshAccelerationCount, color: '#3b82f6' },
    { name: 'Freinage', score: score.harshBrakingScore, count: score.harshBrakingCount, color: '#ef4444' },
    { name: 'Virage', score: score.harshCorneringScore, count: score.harshCorneringCount, color: '#f59e0b' },
    { name: 'Vitesse', score: score.overspeedScore, count: score.overspeedCount, color: '#8b5cf6' },
    { name: 'Ralenti', score: score.idleScore, count: score.idleCount, color: '#64748b' },
    { name: 'Carburant', score: score.fuelScore, count: 0, color: '#10b981' },
  ];

  // Données donut pour le score global
  const donutData = [
    { name: 'Score', value: score.overallScore },
    { name: 'Reste', value: 100 - score.overallScore },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center">
              <span className="text-primary-400 font-bold text-lg">
                {driverDisplayName(score.driver).charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{driverDisplayName(score.driver)}</h2>
              <p className="text-sm text-gray-500">
                Rang #{score.rank} - {getScoreLabel(score.overallScore)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Donut score + Sous-scores */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Donut score */}
          <div className="flex flex-col items-center justify-center">
            <ResponsiveContainer width="100%" height={200} minWidth={0}>
              <PieChart>
                <Pie
                  data={donutData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={85}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                >
                  <Cell fill={getScoreColor(score.overallScore)} />
                  <Cell fill="#e5e7eb" />
                </Pie>
                <text
                  x="50%"
                  y="45%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="text-3xl font-bold"
                  fill={getScoreColor(score.overallScore)}
                >
                  {Math.round(score.overallScore)}
                </text>
                <text
                  x="50%"
                  y="58%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="text-xs"
                  fill="#6b7280"
                >
                  /100
                </text>
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Graphique sous-scores */}
          <div>
            <ResponsiveContainer width="100%" height={200} minWidth={0}>
              <BarChart data={subScoresData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} stroke="#cbd5e1" tick={{ fill: '#475569', fontSize: 12 }} />
                <YAxis dataKey="name" type="category" stroke="#cbd5e1" tick={{ fill: '#475569', fontSize: 12 }} width={90} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    color: '#1e293b',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  }}
                  itemStyle={{ color: '#334155' }}
                  formatter={(value) => [`${value}/100`, 'Score']}
                />
                <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                  {subScoresData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Compteurs d'événements */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          <div className="glass-card p-3 text-center">
            <Zap className="h-5 w-5 text-blue-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-gray-900">{score.harshAccelerationCount}</p>
            <p className="text-xs text-gray-500">Accélérations</p>
          </div>
          <div className="glass-card p-3 text-center">
            <ShieldAlert className="h-5 w-5 text-red-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-gray-900">{score.harshBrakingCount}</p>
            <p className="text-xs text-gray-500">Freinages</p>
          </div>
          <div className="glass-card p-3 text-center">
            <CornerDownRight className="h-5 w-5 text-amber-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-gray-900">{score.harshCorneringCount}</p>
            <p className="text-xs text-gray-500">Virages</p>
          </div>
          <div className="glass-card p-3 text-center">
            <Timer className="h-5 w-5 text-purple-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-gray-900">{score.overspeedCount}</p>
            <p className="text-xs text-gray-500">Excès vitesse</p>
          </div>
          <div className="glass-card p-3 text-center">
            <Timer className="h-5 w-5 text-gray-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-gray-900">{score.idleCount}</p>
            <p className="text-xs text-gray-500">Ralentis</p>
          </div>
          <div className="glass-card p-3 text-center">
            <Gauge className="h-5 w-5 text-green-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-gray-900">{score.fuelConsumptionLper100km > 0 ? `${score.fuelConsumptionLper100km}` : '-'}</p>
            <p className="text-xs text-gray-500">L/100km</p>
          </div>
        </div>

        {/* Distance & Total événements */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="glass-card p-3">
            <div className="flex items-center gap-3">
              <Route className="h-5 w-5 text-purple-400" />
              <div>
                <p className="text-lg font-bold text-gray-900">{Math.round(score.distanceKm)} km</p>
                <p className="text-xs text-gray-500">Distance parcourue</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              <div>
                <p className="text-lg font-bold text-gray-900">{score.totalEvents}</p>
                <p className="text-xs text-gray-500">Événements total</p>
              </div>
            </div>
          </div>
        </div>

        {/* Graphique tendance */}
        {history.length > 1 && (
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">{t('scoring.trend_30d_title')}</h3>
            <ResponsiveContainer width="100%" height={250} minWidth={0}>
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  stroke="#cbd5e1"
                  tick={{ fill: '#475569', fontSize: 11 }}
                  tickFormatter={(d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                />
                <YAxis
                  domain={[0, 100]}
                  stroke="#cbd5e1"
                  tick={{ fill: '#475569', fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    color: '#1e293b',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  }}
                  itemStyle={{ color: '#334155' }}
                  labelFormatter={(d) => new Date(d).toLocaleDateString('fr-FR')}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  formatter={((value: any, name: any) => {
                    const labels: Record<string, string> = {
                      overallScore: 'Score global',
                      harshAccelerationScore: 'Accélération',
                      harshBrakingScore: 'Freinage',
                      harshCorneringScore: 'Virage',
                      overspeedScore: 'Vitesse',
                      idleScore: 'Ralenti',
                      fuelScore: 'Carburant',
                    };
                    return [Math.round(value), labels[name] || name];
                  }) as any}
                />
                <Legend
                  formatter={(value) => {
                    const labels: Record<string, string> = {
                      overallScore: 'Global',
                      harshAccelerationScore: 'Accélération',
                      harshBrakingScore: 'Freinage',
                      harshCorneringScore: 'Virage',
                      overspeedScore: 'Vitesse',
                      idleScore: 'Ralenti',
                      fuelScore: 'Carburant',
                    };
                    return <span style={{ color: '#475569', fontSize: 12 }}>{labels[value] || value}</span>;
                  }}
                />
                <Line type="monotone" dataKey="overallScore" stroke="#22c55e" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="harshAccelerationScore" stroke="#3b82f6" strokeWidth={1} dot={false} />
                <Line type="monotone" dataKey="harshBrakingScore" stroke="#ef4444" strokeWidth={1} dot={false} />
                <Line type="monotone" dataKey="harshCorneringScore" stroke="#f59e0b" strokeWidth={1} dot={false} />
                <Line type="monotone" dataKey="overspeedScore" stroke="#8b5cf6" strokeWidth={1} dot={false} />
                <Line type="monotone" dataKey="idleScore" stroke="#64748b" strokeWidth={1} dot={false} />
                <Line type="monotone" dataKey="fuelScore" stroke="#10b981" strokeWidth={1} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
